import {createStore} from 'zustand/vanilla';
import type {Nullable} from '#types/general.js';
import type {UUID} from '#types/strings.js';
import type {JourneyNodeType} from '#types/journeys.js';

export type GenericKey = UUID | string;

export enum JourneyErrorKey {
  FIELD_WRONG_TYPE = 'field_wrong_type',
  FIELD_NOT_PRESENT = 'field_not_present',
  EMPTY_NODE = 'empty_node',
}

export const ERROR_SOURCES = {
  BACKEND: 'backend',
  FRONTEND: 'frontend',
} as const;

export type ErrorSource = (typeof ERROR_SOURCES)[keyof typeof ERROR_SOURCES];

interface JourneyError {
  message: string;
  fields: string[];
  error_key: JourneyErrorKey;
  title: string;
  severity: 'error' | 'warning';
  source: ErrorSource;
}

export interface JourneyNodeErrors {
  errors: JourneyError[];
  node_name: string;
  node_type: JourneyNodeType;
}

export interface JourneyErrors {
  [nodeId: string]: JourneyNodeErrors;
}

export type ErrorState = {
  errors: Map<string, JourneyNodeErrors>;
  selectedNodeId: Nullable<GenericKey>;
  setBackendErrors: (errors: JourneyErrors) => void;
  addErrors: (errors: JourneyErrors) => void;
  setSelectedNodeId: (id: Nullable<GenericKey>) => void;
  removeError: (nodeId: string, errorKey: JourneyErrorKey) => void;
};

export function isJourneyErrors(value: unknown): value is JourneyErrors {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return Object.values(value).every(
    nodeError =>
      typeof nodeError === 'object' &&
      Array.isArray(nodeError.errors) &&
      typeof nodeError.node_name === 'string' &&
      typeof nodeError.node_type === 'string'
  );
}

function addSourceToErrors(
  errors: JourneyError[],
  source: ErrorSource
): JourneyError[] {
  return errors.map(error => ({
    ...error,
    source,
  }));
}

export const errorStore = createStore<ErrorState>()(set => ({
  errors: new Map(),
  selectedNodeId: null,

  setSelectedNodeId(id: Nullable<GenericKey>) {
    set(state => ({
      ...state,
      selectedNodeId: id,
    }));
  },

  setBackendErrors(errors: JourneyErrors) {
    set(state => {
      const newErrors = new Map(state.errors);

      for (const [nodeId, nodeErrors] of newErrors) {
        const frontendErrors = nodeErrors.errors.filter(
          error => error.source === ERROR_SOURCES.FRONTEND
        );

        if (frontendErrors.length === 0) {
          newErrors.delete(nodeId);
        } else {
          newErrors.set(nodeId, {
            ...nodeErrors,
            errors: frontendErrors,
          });
        }
      }

      for (const nodeId in errors) {
        const nodeErrors = errors[nodeId];
        const backendErrors = addSourceToErrors(
          nodeErrors.errors,
          ERROR_SOURCES.BACKEND
        );
        const existingNodeErrors = newErrors.get(nodeId);

        if (existingNodeErrors) {
          newErrors.set(nodeId, {
            ...existingNodeErrors,
            errors: [...existingNodeErrors.errors, ...backendErrors],
          });
        } else {
          newErrors.set(nodeId, {
            ...nodeErrors,
            errors: backendErrors,
          });
        }
      }

      return {
        ...state,
        errors: newErrors,
      };
    });
  },

  addErrors(errors: JourneyErrors) {
    set(state => {
      const newErrors = new Map(state.errors);

      for (const nodeId in errors) {
        const nodeErrors = errors[nodeId];
        const frontendErrors = addSourceToErrors(
          nodeErrors.errors,
          ERROR_SOURCES.FRONTEND
        );

        const existingNodeErrors = newErrors.get(nodeId);

        if (existingNodeErrors) {
          const mergedErrors = [...existingNodeErrors.errors];

          frontendErrors.forEach(newError => {
            const errorExists = mergedErrors.some(
              existingError =>
                existingError.error_key === newError.error_key &&
                existingError.source === ERROR_SOURCES.FRONTEND
            );

            if (!errorExists) {
              mergedErrors.push(newError);
            }
          });

          newErrors.set(nodeId, {
            ...existingNodeErrors,
            errors: mergedErrors,
          });
        } else {
          newErrors.set(nodeId, {
            ...nodeErrors,
            errors: frontendErrors,
          });
        }
      }

      return {
        ...state,
        errors: newErrors,
      };
    });
  },

  removeError(nodeId: string, errorKey: JourneyErrorKey) {
    set(state => {
      const newErrors = new Map(state.errors);
      const currentNodeErrors = newErrors.get(nodeId);

      if (!currentNodeErrors) {
        return state;
      }

      const filteredErrors = currentNodeErrors.errors.filter(
        error => error.error_key !== errorKey
      );

      if (filteredErrors.length === 0) {
        newErrors.delete(nodeId);
      } else {
        newErrors.set(nodeId, {
          ...currentNodeErrors,
          errors: filteredErrors,
        });
      }

      return {
        ...state,
        errors: newErrors,
      };
    });
  },
}));
